import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'antd';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const InsertExtensionModal = ({endDate="", visible, handleClose, onExtendSubmit }) => {
    const formatDate = (dateString) => {
        const date = new Date(dateString);

        // Get the components of the date
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-indexed, so add 1
        const day = date.getDate().toString().padStart(2, '0');
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const seconds = '00'; // Set seconds to '00' as per the requested format

        // Return the formatted date string
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    useEffect(()=>{
        if(endDate){
            formik.setFieldValue('proposedEndDateTime',endDate)
        }
    },[endDate])

    const formik = useFormik({
        initialValues: {
            proposedEndDateTime: endDate,
            newEndDateTime: '',
            extensionReason: '',
        },
        validationSchema: Yup.object({
            proposedEndDateTime: Yup.string().required('Proposed End Date and Time is required'),
            newEndDateTime: Yup.string()
                .required('New End Date and Time is required').test('is-later', 'New End Date must be later than Proposed End Date', function (value) {
                    const { proposedEndDateTime } = this.parent;
    
                    if (!proposedEndDateTime || !value) {
                        return true; // Skip validation if either date is missing (handled by required)
                    }
    
                    const startDateTime = new Date(proposedEndDateTime);
                    const endDateTime = new Date(value);
    
                    // Check if end date is after start date or if dates are the same but time is later
                    return (
                        endDateTime > startDateTime ||
                        (startDateTime.toDateString() === endDateTime.toDateString() &&
                            endDateTime.getTime() > startDateTime.getTime())
                    );
                }),
            extensionReason: Yup.string().required('Extension Reason is required'),
        }),
        onSubmit: (values, { resetForm }) => {
            onExtendSubmit({
                proposedEndDateTime: formatDate(values?.proposedEndDateTime),
                newEndDateTime: formatDate(values?.newEndDateTime),
                extensionReason: values?.extensionReason,
            },resetForm);
        },
    });

    return (
        <Modal
            title="Insert Extension"
            visible={visible}
            onCancel={() => {
                formik.setFieldValue('proposedEndDateTime','')
                formik.resetForm();
                handleClose();
            }}
            footer={null}
            destroyOnClose
            centered
            width={400}
            className="custom-modal-header p-0"
            maskClosable={false}
        >
            <form onSubmit={formik.handleSubmit}>
                <div role="document">
                    <div className="row g-3">
                        {/* Proposed End Date and Time Field */}
                        <div className="col-12 position-relative">
                            <label className="form-label fw-bold fs-6">Proposed End Date and Time</label>
                            <input
                                type="datetime-local"
                                className="form-control form-control-sm"
                                name="proposedEndDateTime"
                                value={formik.values.proposedEndDateTime}
                                onChange={formik.handleChange}
                                readOnly
                            />
                            {formik.touched.proposedEndDateTime && formik.errors.proposedEndDateTime && (
                                <span style={{ color: 'red' }} role="alert">
                                    {formik.errors.proposedEndDateTime}
                                </span>
                            )}
                        </div>

                        {/* New End Date and Time Field */}
                        <div className="col-12 position-relative">
                            <label className="form-label fw-bold fs-6">New End Date and Time <span className="text-danger">*</span></label>
                            <input
                                type="datetime-local"
                                className="form-control form-control-sm"
                                name="newEndDateTime"
                                value={formik.values.newEndDateTime}
                                onChange={formik.handleChange}
                            />
                            {formik.touched.newEndDateTime && formik.errors.newEndDateTime && (
                                <span style={{ color: 'red' }} role="alert">
                                    {formik.errors.newEndDateTime}
                                </span>
                            )}
                        </div>

                        {/* Extension Reason Field */}
                        <div className="col-12 position-relative">
                            <label className="form-label fw-bold fs-6">Extension Reason <span className="text-danger">*</span></label>
                            <textarea
                                className="form-control form-control-sm"
                                style={{ height: '80px' }}
                                placeholder="Enter reason for extension"
                                name="extensionReason"
                                value={formik.values.extensionReason}
                                onChange={formik.handleChange}
                            />
                            {formik.touched.extensionReason && formik.errors.extensionReason && (
                                <span style={{ color: 'red' }} role="alert">
                                    {formik.errors.extensionReason}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="modal-footer mt-3">
                        <button type="button" className="btn btn-light btn-sm" onClick={handleClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary btn-sm ms-3">
                            Submit Extension
                        </button>
                    </div>
                </div>
            </form>
        </Modal>
    );
};

InsertExtensionModal.propTypes = {
    visible: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
};

export default InsertExtensionModal;
